import React from 'react';
import { 
  Server, Globe, Database, Box, HardDrive, 
  Cloud, Activity, Clock, CheckCircle, AlertTriangle, XCircle, ChevronRight, Edit, Trash2,
} from 'lucide-react';

const ResourceCard = ({ resource, onClick, onDelete, onEdit }) => {
  const { id, type, name, status, createdAt } = resource;
  const isPending = status?.toLowerCase() === 'pending';
  
  // Get appropriate icon based on resource type
  const getIcon = () => {
    switch(type) {
      case 'Frontend App':
        return <Globe className="w-5 h-5 text-indigo-600" />;
      case 'Backend Service':
        return <Server className="w-5 h-5 text-indigo-600" />;
      case 'Database':
        return <Database className="w-5 h-5 text-indigo-600" />;
      case 'Redis Cluster':
        return <HardDrive className="w-5 h-5 text-indigo-600" />;
      case 'File Storage':
        return <Cloud className="w-5 h-5 text-indigo-600" />;
      default:
        return <Box className="w-5 h-5 text-indigo-600" />;
    }
  };

  // Get status configuration
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
          label: 'Active'
        };
      case 'pending':
        return {
          icon: <AlertTriangle className="w-4 h-4" />,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
          label: 'Pending'
        };
      case 'inactive':
        return {
          icon: <XCircle className="w-4 h-4" />,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
          label: 'Inactive'
        };
      default:
        return {
          icon: <Activity className="w-4 h-4" />,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          label: 'N/A'
        };
    }
  };

  const statusConfig = getStatusConfig(status);
  const iconComponent = getIcon();
  const resourceType = type || 'Unknown Resource';

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(resource);
  };



  return (
  <div className="relative group">
    {isPending && (
      <div className="absolute -top-3 -right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button 
          onClick={handleEdit}
          className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors duration-200"
          aria-label="Edit server"
        >
          <Edit className="w-4 h-4 text-blue-600" />
        </button>
        <button 
          onClick={handleDelete}
          className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors duration-200"
          aria-label="Delete server"
        >
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      </div>
    )}

    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 group w-full md:w-96 lg:w-full"
    >
      {/* Top status border */}
      <div className={`h-1 w-full ${statusConfig.bgColor}`}></div>

      {/* Card Body */}
      <div className="p-6 space-y-4">
        {/* Header with Icon and Resource Name */}
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors">
            {iconComponent}
          </div>
          <h3 className="text-lg font-semibold text-gray-800 break-words whitespace-normal">
            {name}
          </h3>
        </div>

        {/* Status and Resource Type - ensure they stay on one line */}
        <div className="flex items-center space-x-2">
          {/* Status Badge */}
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
            {statusConfig.icon}
            <span className="ml-1">{statusConfig.label}</span>
          </div>

          {/* Resource Type Badge */}
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 truncate">
            <Activity className="w-4 h-4 flex-shrink-0 mr-1" />
            <span className="truncate">{resourceType}</span>
          </div>
        </div>

        {/* Created At Time */}
        <div className="text-sm text-gray-600">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2 text-gray-400" />
            <div className="flex flex-col">
              <p>Created: {createdAt}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with CTA */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-600">
            View Details
          </span>
          <ChevronRight className="w-5 h-5 text-indigo-600" />
        </div>
      </div>
    </div>
  </div>
  );
};

export default ResourceCard;